import RefreshIcon from '@mui/icons-material/Refresh';
import { Button } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import * as _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import FuelTable from '../../../components/fuel-table/FuelTable';
import { AssetEventActionTypes } from '../../../enums';
import type { Item, VariantStatus } from '../../../models/item';
import { FuelPortalService } from '../../../services';
import { TranscodeJobManagerService } from '../../../services/transcode-job-manager-service';
import { applicationDataSelector } from '../../../store/slices/application-data';
import useAppUtils from '../../../utils/appUtils';
import clientUtils from '../../../utils/clientUtils';
import { dateUtils } from '../../../utils/dateUtils';

const STALLED_TIMEOUT_IN_SECONDS = 43200; // 12 hours
const MIN_STALLED_TIMEOUT_IN_SECONDS = 3600; // 1 hours

interface IProps {
  assetFormValues: any; //  react hook form
}

const columnHelper = createColumnHelper<any>();

const AssetVariantList = ({ assetFormValues }: IProps) => {
  const { alert, vnConfirm } = useAppUtils();
  const { ouData, schemas, categories } = useSelector(applicationDataSelector);
  //   const { permissions, fuelUserAccessData } = useSelector(fuelUserAccessSliceSelector);
  //   const flags = useFlags();

  //   const { watch } = assetFormValues;
  //   // const [selectedTags, setSelectedTags] = useState(assetFormValues.getValues('tags'));
  const [variantStatuses, setVariantStatuses] = useState<VariantStatus[]>([]);

  const stateRef = useRef<{ variantStatuses: VariantStatus[] }>({ variantStatuses: [] });

  useEffect(() => {
    // init code here
    // console.log('AssetVariantList init', { newAssetForm });

    init();
  }, []);

  const init = () => {
    // console.log('AssetVariantList init', { assetFormValues });
    const newAssetForm = assetFormValues.getValues();
    populateVariantStatuses(newAssetForm);
  };

  const populateVariantStatuses = async (newAssetForm) => {
    // console.log('populateVariantStatuses', { newAssetForm });
    let myVariantStatuses: VariantStatus[] = [];
    const itemVariantStatuses = newAssetForm?.originalAssetData?.variantStatuses;
    if (itemVariantStatuses != null && itemVariantStatuses.length > 0) {
      const item = newAssetForm?.originalAssetData;
      myVariantStatuses = await populateAssetVariantsFromItem(item);
    } else {
      myVariantStatuses = await populateAssetVariantsFromScratch(newAssetForm);
    }
    stateRef.current.variantStatuses = myVariantStatuses;
    setVariantStatuses(myVariantStatuses);
  };

  const populateAssetVariantsFromItem = async (item: Item) => {
    // console.log('populateAssetVariantsFromItem', { item });
    let myVariantStatuses: VariantStatus[] = [];
    if (item.variantStatuses != null && item.variantStatuses.length > 0) {
      myVariantStatuses = item.variantStatuses;
    }

    if (item.source != 'NestedChannel' && item.source != 'ClipGroup') {
      const clipId = item.id;
      let checkTJMJobs = false;
      let hasJobsForRetry = false;
      const allowRestart = !clientUtils.isNullOrWhitespace(clipId);

      for (let i = 0; i < myVariantStatuses.length; i++) {
        const v = myVariantStatuses[i];
        if (v.status == 'Missing') {
          v.allowRestart = true;
        }
        if (v.status != 'Completed') {
          checkTJMJobs = true;
        }
      }

      if (!clientUtils.isNullOrWhitespace(clipId) && checkTJMJobs) {
        const jobsResponse = await TranscodeJobManagerService.fetchJobs(clipId);
        // console.log('jobsResponse', { jobsResponse });
        const conformJob = jobsResponse.find((x) => x.jobType == 'Conform');
        if (conformJob) {
          const { lastUpdated } = conformJob;
          // console.log('lastUpdated', { lastUpdated });
          const diff = dateUtils.getDifferenceDurationByUnit(new Date(), lastUpdated, 'seconds');

          // console.log('diff', { diff, STALLED_TIMEOUT_IN_SECONDS });
          let hasStalledJobs = false;
          if (diff > STALLED_TIMEOUT_IN_SECONDS) {
            hasStalledJobs = true;
          }

          // console.log('extra duration check', { item });
          if (!hasStalledJobs && item.duration != null && _.isNumber(item.duration)) {
            const itemMaxStallSeconds = item.duration * 4;
            // console.log('extra duration check', { diff, itemMaxStallSeconds, MIN_STALLED_TIMEOUT_IN_SECONDS });
            if (diff > Math.max(MIN_STALLED_TIMEOUT_IN_SECONDS, itemMaxStallSeconds)) {
              hasStalledJobs = true;
            }
          }

          myVariantStatuses.forEach((x) => {
            if (hasStalledJobs && x.status == 'Started') {
              x.status = 'Stalled';
              x.jobId = conformJob.jobId;
              x.lastUpdated = lastUpdated;
              x.allowRestart = allowRestart;
              hasJobsForRetry = true;
            }
            if (x.status == 'Failed' || x.status == 'Invalid') {
              x.jobId = conformJob.jobId;
              x.lastUpdated = lastUpdated;
              x.allowRestart = allowRestart;
              hasJobsForRetry = true;
            }
          });
        }
      }

      // // only allow conform if no failed jobs, or the new variants won't succeed.
      // if (!hasJobsForRetry) {
      //   for (let i = 0; i < myVariantStatuses.length; i++) {
      //     const v = myVariantStatuses[i];
      //     if (v.status == 'Missing') {
      //       v.allowRestart = true;
      //     }
      //   }
      // }
    }

    return myVariantStatuses;
  };

  const populateAssetVariantsFromScratch = async (newAssetForm) => {
    // console.log('populateAssetVariantsFromScratch', { newAssetForm });
    const myVariantStatuses: VariantStatus[] = [];
    const clipId = newAssetForm.id;
    let checkTJMJobs = false;

    // Fill in the variants from the asset
    const itemVariantStatuses = newAssetForm?.originalAssetData?.data?.clipHLSData?.variantStatuses;
    if (itemVariantStatuses != null) {
      // console.log('AssetVariantList itemVariantStatuses', { itemVariantStatuses });

      for (const variantCode in itemVariantStatuses) {
        if (Object.prototype.hasOwnProperty.call(itemVariantStatuses, variantCode)) {
          const status = itemVariantStatuses[variantCode];
          // console.log('itemVariantStatuses', { variantCode, status });
          if (status != 'Completed') {
            checkTJMJobs = true;
          }
          myVariantStatuses.push({ clipId, variantCode, status, allowRestart: false });
        }
      }
    }

    // Now fill in any ingest variants missing from the OU Ingest Variants
    // console.log('ouData', { ouData });
    const ingestVariants: string[] = ouData?.metaData?.ingestVariants;
    // console.log('ingestVariants', { ingestVariants });
    if (ingestVariants != null && ingestVariants.length > 0) {
      for (let i = 0; i < ingestVariants.length; i++) {
        const v = ingestVariants[i];
        const foundIndex = myVariantStatuses.findIndex((x) => x.variantCode == v);
        if (foundIndex == -1) {
          checkTJMJobs = true;
          const allowRestart = !!(
            !clientUtils.isNullOrWhitespace(clipId) &&
            newAssetForm.source != 'NestedChannel' &&
            newAssetForm.source != 'ClipGroup'
          );
          myVariantStatuses.unshift({ clipId, variantCode: v, status: 'Missing', allowRestart });
        }
      }
    }

    if (
      !clientUtils.isNullOrWhitespace(clipId) &&
      newAssetForm.source != 'NestedChannel' &&
      newAssetForm.source != 'ClipGroup' &&
      checkTJMJobs
    ) {
      const jobsResponse = await TranscodeJobManagerService.fetchJobs(clipId);
      // console.log('jobsResponse', { jobsResponse });
      const conformJob = jobsResponse.find((x) => x.jobType == 'Conform');
      if (conformJob) {
        const { lastUpdated } = conformJob;
        // console.log('lastUpdated', { lastUpdated });
        const diff = dateUtils.getDifferenceDurationByUnit(new Date(), lastUpdated, 'hours');

        // console.log('diff', { diff });
        let hasStalledJobs = false;
        if (diff > 12) {
          hasStalledJobs = true;
        }

        const allowRestart = !clientUtils.isNullOrWhitespace(clipId);

        myVariantStatuses.forEach((x) => {
          if (hasStalledJobs && x.status == 'Started') {
            x.status = 'Stalled';
            x.jobId = conformJob.jobId;
            x.lastUpdated = lastUpdated;
            x.allowRestart = allowRestart;
          }
          if (x.status == 'Missing' || x.status == 'Failed' || x.status == 'Invalid') {
            x.jobId = conformJob.jobId;
            x.lastUpdated = lastUpdated;
            x.allowRestart = allowRestart;
          }
        });
      }
    }

    return myVariantStatuses;
  };

  const onActionClick = async (action: AssetEventActionTypes, obj: VariantStatus) => {
    // console.log('onActionClick', { action, obj });
    if (action === AssetEventActionTypes.Restart) {
      try {
        if (obj.status == 'Missing') {
          const myVariantStatuses = _.cloneDeep(stateRef.current.variantStatuses);
          // console.log('onActionClick', { myVariantStatuses });
          // guard against restarting ingest when there are incomplete variants
          for (let i = 0; i < myVariantStatuses.length; i++) {
            if (myVariantStatuses[i].status != 'Missing' && myVariantStatuses[i].status != 'Completed') {
              alert(
                'You cannot add missing variants until all current variants are in COMPLETED status.  Please first restart any failed, invalid, or stalled variants.  If the problem persists please contact support.',
                'warning'
              );
              return;
            }
          }
          const clipId = assetFormValues.getValues('id');
          // console.log('onActionClick', { clipId });
          if (clientUtils.isNullOrWhitespace(clipId)) {
            alert('No clipId found to restart.  Please contact support.');
            return;
          }
          const confirmed = await vnConfirm(`Are you sure you want to conform the ${obj.variantCode} variant?`);
          if (!confirmed) {
            return;
          }
          const response = await FuelPortalService.startIngestByClipId(clipId);
        } else {
          // for Failed, Invalid or Stalled variants
          const { jobId } = obj;
          if (jobId == null) {
            alert('No jobId found to restart.  Please contact support.');
            return;
          }
          const confirmed = await vnConfirm(`Are you sure you want to restart the ${obj.variantCode} variant?`);
          if (!confirmed) {
            return;
          }
          const response = await TranscodeJobManagerService.restartJob(jobId);
        }
        alert('Job has been started. Please allow some time for the status of the Asset to be updated.');
      } catch (ex) {
        console.error(`Failed to retry asset job.`, { ex });
        alert(`Failed to retry asset job.`, 'error');
      }
    }
  };

  const columns = [
    columnHelper.accessor('variantCode', {
      align: 'left',
      header: () => 'Variant',
      width: 200,
      sortable: true,
      footer: (props) => props.column.id,
      cell: (cell) => {
        return (
          <div title={cell.getValue()}>
            <div className="text-ellipses">{cell.getValue()}</div>
          </div>
        );
      },
    }),
    columnHelper.accessor('status', {
      align: 'left',
      header: 'Status',
      footer: (props) => props.column.id,
      width: 150,
      sortable: false,
      cell: (cell) => cell.getValue(),
    }),
    columnHelper.accessor('lastUpdated', {
      header: () => `Last Updated (${ouData.timezoneAbbreviation})`,
      width: 150,
      sortable: true,
      cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
    }),
    columnHelper.accessor('actions', {
      align: 'left',
      header: 'Actions',
      footer: (props) => props.column.id,
      width: 100,
      sortable: false,
      cell: (cell) => (
        <div>
          {cell.row.original.allowRestart === true && (
            <Button
              title="Restart Job"
              size="small"
              variant="outlined"
              // disabled={audit.restartRequestSubmitted}
              // color={audit.hasError ? 'error' : 'primary'}
              onClick={() => onActionClick(AssetEventActionTypes.Restart, cell.row.original)}
            >
              <RefreshIcon />
            </Button>
          )}
        </div>
      ),
    }),
  ];

  return (
    <div id="asset-variant-list">
      <FuelTable columnHelper={columnHelper} data={variantStatuses} defaultColumns={columns} loading={false} hidePagination />
    </div>
  );
};

export default AssetVariantList;
